import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Calendar, Check, Loader2 } from "lucide-react";
import {
  ExportCurve,
  ImportCurve,
  Add,
  DocumentDownload,
  DocumentText,
} from "iconsax-reactjs";
import AskGenziButton from "./AskGenziButton";
import api from "../../services/api";

const MediaPlayback = () => {
  const navigate = useNavigate();
  const [selectedFilters, setSelectedFilters] = useState({
    speaker: "Speaker",
    tag: "Tag",
  });

  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchMeetings();
  }, [currentPage]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const response = await api.getMeetings({
        status: "completed",
        page: currentPage,
        limit: 10,
      });

      if (response.success) {
        setMeetings(response.meetings || []);
        setTotalPages(
          Math.ceil((response.total || response.meetings.length) / 10)
        );
      }
    } catch (error) {
      console.error("Failed to fetch meetings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedRows(new Set(meetings.map((m) => m.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleRowSelect = (id, checked) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
    setSelectAll(newSelected.size === meetings.length);
  };

  const handleViewDetails = (meetingId) => {
    navigate(`/media-playback/${meetingId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getPlatformLogo = (platformName) => {
    switch (platformName?.toLowerCase()) {
      case "zoom":
        return "/assets/zoom.png";
      case "meet":
      case "google meet":
        return "/assets/meets.png";
      case "teams":
      case "microsoft teams":
        return "/assets/teams.png";
      default:
        return null;
    }
  };

  const extractTags = (meeting) => {
    const tags = [];

    if (meeting.insights?.tags && Array.isArray(meeting.insights.tags)) {
      return meeting.insights.tags.slice(0, 2).map((tag, idx) => ({
        name: tag,
        color:
          idx === 0 ? "bg-[#053321] text-white" : "bg-[#1F235B] text-white",
      }));
    }

    if (
      meeting.insights?.keyTopics &&
      Array.isArray(meeting.insights.keyTopics)
    ) {
      return meeting.insights.keyTopics.slice(0, 2).map((topic, idx) => ({
        name: topic,
        color:
          idx === 0 ? "bg-[#053321] text-white" : "bg-[#1F235B] text-white",
      }));
    }

    return [{ name: "Meeting", color: "bg-gray-600 text-white" }];
  };

  const getSummary = (meeting) => {
    if (meeting.insights?.summary) {
      return meeting.insights.summary.substring(0, 100) + "...";
    }
    return "No summary available";
  };

  const CallRow = ({ meeting }) => {
    const logoPath = getPlatformLogo(meeting.platform);
    const isSelected = selectedRows.has(meeting.id);
    const tags = extractTags(meeting);
    const remainingTags = (meeting.insights?.tags?.length || 0) - 2;

    return (
      <tr className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
        <td className="py-4 px-4">
          <div className="relative">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => handleRowSelect(meeting.id, e.target.checked)}
              className="appearance-none w-4 h-4 rounded border border-gray-600 bg-[#0C0E12] checked:bg-purple-500 checked:border-purple-500 focus:ring-purple-500 focus:ring-2"
            />
            {isSelected && (
              <Check
                size={11}
                className="absolute top-1 left-0.5 text-white pointer-events-none"
              />
            )}
          </div>
        </td>
        <td className="py-4 px-4 text-white font-medium">
          <div className="flex items-center gap-2">
            {logoPath ? (
              <img
                src={logoPath}
                alt={`${meeting.platform} logo`}
                className="w-8 h-8 object-contain"
              />
            ) : (
              <div className="w-5 h-5 bg-gray-600 rounded flex items-center justify-center">
                <span className="text-xs text-gray-400">?</span>
              </div>
            )}
            <span className="text-gray-400">
              {meeting.title || "Untitled Meeting"}
            </span>
          </div>
        </td>

        <td className="py-4 px-4 text-gray-400">
          {formatDate(meeting.scheduledAt || meeting.createdAt)}
        </td>
        <td className="py-4 px-4 text-gray-400">{getSummary(meeting)}</td>
        <td className="py-4 px-4">
          <div className="flex flex-wrap gap-1">
            {tags.map((tag, idx) => (
              <span
                key={idx}
                className={`px-3 py-1 rounded-full text-xs font-medium ${tag.color}`}
              >
                {tag.name}
              </span>
            ))}
            {remainingTags > 0 && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-600 text-white">
                +{remainingTags}
              </span>
            )}
          </div>
        </td>

        <td className="py-4 px-4 text-right">
          <button
            onClick={() => handleViewDetails(meeting.id)}
            className="text-gray-400 hover:text-white transition-colors p-1 underline"
          >
            <DocumentText size={20} />
          </button>
          <button
            onClick={() => api.downloadTranscript(meeting.id, "txt")}
            className="text-gray-400 hover:text-white transition-colors p-1 underline"
          >
            <DocumentDownload size={20} />
          </button>
          <button className="text-gray-400 hover:text-white transition-colors p-1 underline">
            <ExportCurve size={20} />
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Media Playback
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Manage all your audio files, summaries and AI insights.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0">
              <ImportCurve size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Bulk import</span>
            </button>

            <button
              onClick={() => navigate("/schedule-meeting")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                    0px 36px 32px -16px #7F56D936,
                    0px 8px 16px 0px #7F56D91F,
                    0px 2px 4px -8px #7F56D914,
                    0px 8px 24px -16px #FFFFFF3D inset,
                    0px -24px 32px 0px #FFFFFF38 inset,
                    0px 0px 12px 0px #FFFFFF inset
                  `,
                borderImageSlice: "1",
                transition: "all 0.3s ease",
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Schedule Meeting</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {Object.entries(selectedFilters).map(([key, value]) => (
              <button
                key={key}
                className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors"
              >
                {value}
                <ChevronDown size={14} />
              </button>
            ))}
          </div>

          <button className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors self-start lg:self-auto">
            <Calendar size={14} />
            Select dates
          </button>
        </div>

        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 mb-8">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-purple-500" />
              </div>
            ) : meetings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-gray-400 mb-4">No meetings found</p>
                <button
                  onClick={() => navigate("/schedule-meeting")}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  Schedule Your First Meeting
                </button>
              </div>
            ) : (
              <table className="w-full min-w-[800px]">
                <thead className="bg-[#13161B]">
                  <tr className="border-b border-[#22262F]">
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm w-12">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="appearance-none w-4 h-4 rounded border border-gray-600 bg-[#0C0E12] checked:bg-purple-500 checked:border-purple-500 focus:ring-purple-500 focus:ring-2"
                        />
                        {selectAll && (
                          <Check
                            size={11}
                            className="absolute top-1 left-0.5 text-white pointer-events-none"
                          />
                        )}
                      </div>
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Title
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Date
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Summary
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Tags
                    </th>
                    <th className="text-right py-4 px-4 text-gray-400 font-medium text-sm"></th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((meeting) => (
                    <CallRow key={meeting.id} meeting={meeting} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!loading && meetings.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t border-gray-700 gap-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <span className="text-gray-400 text-sm">
                Page {currentPage} of {totalPages}
              </span>
            </div>
          )}
        </div>

        <AskGenziButton />
      </div>
    </div>
  );
};

export default MediaPlayback;
